import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Package, ShoppingBag, CreditCard,
  BarChart2, Settings, Menu, X, LogOut, Bell, Search, Sparkles, Megaphone,
  LayoutTemplate, UserSquare2, Handshake, PanelBottom, FileText, ShieldCheck, FolderTree,
  Store, ExternalLink,
} from 'lucide-react';
import { useAdminStore, adminCan, type StaffPermission } from '@/store/admin-store';
import { STOREFRONT_URL } from '@/lib/cms';
import { cn } from '@nepal-ecom/ui';
import { Avatar, AvatarFallback } from '@nepal-ecom/ui';
import { Button } from '@nepal-ecom/ui';
import { Input } from '@nepal-ecom/ui';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@nepal-ecom/ui';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  end?: boolean;
  superAdminOnly?: boolean;
  /** Visible only to admin/super_admin (no staff permission grants it). */
  adminOnly?: boolean;
  /** Staff need any one of these permissions; admins/super-admins always see it. */
  anyOf?: StaffPermission[];
}

const NAV_SECTIONS: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Overview',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true, anyOf: ['analytics.view'] },
      { to: '/analytics', icon: BarChart2, label: 'Analytics', anyOf: ['analytics.view'] },
    ],
  },
  {
    heading: 'Store',
    items: [
      { to: '/products', icon: Package, label: 'Catalog', anyOf: ['products.manage'] },
      { to: '/categories', icon: FolderTree, label: 'Categories', anyOf: ['products.manage'] },
      { to: '/orders', icon: ShoppingBag, label: 'Orders', anyOf: ['orders.view', 'orders.manage'] },
      { to: '/vendors', icon: Store, label: 'Vendors', anyOf: ['vendors.manage', 'vendors.approve'] },
      { to: '/users', icon: Users, label: 'Customers', anyOf: ['users.view'] },
      { to: '/payments', icon: CreditCard, label: 'Payment methods', anyOf: ['payments.view'] },
    ],
  },
  {
    heading: 'Content',
    items: [
      { to: '/roadblockers', icon: Megaphone, label: 'Roadblockers', anyOf: ['roadblockers.manage'] },
      { to: '/content/homepage', icon: LayoutTemplate, label: 'Homepage CMS', anyOf: ['cms.manage'] },
      { to: '/content/hero', icon: LayoutTemplate, label: 'Hero Slider', anyOf: ['cms.manage'] },
      { to: '/content/festival', icon: Sparkles, label: 'Promotional Banner', anyOf: ['cms.manage'] },
      { to: '/content/team', icon: UserSquare2, label: 'Team Members', anyOf: ['cms.manage'] },
      { to: '/content/partners', icon: Handshake, label: 'Partners & Backers', anyOf: ['cms.manage'] },
      { to: '/content/footer', icon: PanelBottom, label: 'Footer CMS', anyOf: ['cms.manage'] },
      { to: '/content/info-pages', icon: FileText, label: 'Info Pages', anyOf: ['cms.manage'] },
    ],
  },
  {
    heading: 'Admin',
    items: [
      { to: '/staff', icon: ShieldCheck, label: 'Staff & Roles', superAdminOnly: true },
      { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true },
    ],
  },
];

/** Whether the current user may see a nav item. Defaults to admin-only: a staff
 *  member only sees an item that explicitly lists a permission they hold, so a
 *  new item with no gate is never accidentally exposed to all staff. */
function canSeeNav(item: NavItem, admin: ReturnType<typeof useAdminStore.getState>['admin']): boolean {
  const isSuperAdmin = admin?.role === 'super_admin';
  const isAdmin = admin?.role === 'admin' || isSuperAdmin;
  if (item.superAdminOnly) return isSuperAdmin;
  if (item.adminOnly) return isAdmin;
  if (item.anyOf && item.anyOf.length > 0) return item.anyOf.some((p) => adminCan(admin, p));
  // No explicit gate → admin/super_admin only (safe default).
  return isAdmin;
}

function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { admin } = useAdminStore();

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}

      <aside
        className={cn(
          'sidebar-surface fixed inset-y-0 left-0 w-64 text-slate-300 flex flex-col z-50 transition-transform duration-200 ease-out',
          'lg:translate-x-0 lg:static lg:z-auto',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-sm font-bold shadow-md shadow-primary-900/30">श</div>
            <div className="leading-tight">
              <p className="font-semibold text-white text-sm">Shishyastra</p>
              <p className="text-[11px] text-slate-400 uppercase tracking-wider">Admin Console</p>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden p-1.5 text-slate-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6 scrollbar-hide">
          {NAV_SECTIONS.map((section) => {
            const visibleItems = section.items.filter((item) => canSeeNav(item, admin));
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.heading}>
                <p className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-[0.12em]">
                  {section.heading}
                </p>
                <div className="space-y-0.5">
                  {visibleItems.map(({ to, icon: Icon, label, end }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                          isActive
                            ? 'bg-primary-600/20 text-white shadow-[inset_2px_0_0_theme(colors.primary.500)]'
                            : 'text-slate-300 hover:text-white hover:bg-white/10'
                        )
                      }
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/5">
          <div className="flex items-center gap-3 rounded-lg bg-slate-800/40 px-3 py-2.5">
            <Avatar className="h-9 w-9 ring-2 ring-slate-700">
              <AvatarFallback className="bg-primary-500/20 text-primary-300 text-xs">
                {admin?.name?.[0]?.toUpperCase() || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">{admin?.name || 'Admin'}</p>
              <p className="text-[11px] text-slate-400 capitalize truncate">{admin?.role?.replace('_', ' ') || 'admin'}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { admin, logout } = useAdminStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Global search: routes to the most relevant list based on a simple keyword
  // heuristic, otherwise the catalog. Each list page reads ?search= on load.
  const runSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    const lower = q.toLowerCase();
    let path = '/products';
    if (lower.startsWith('order') || /^#?\d{4,}$/.test(q)) path = '/orders';
    else if (lower.startsWith('vendor') || lower.startsWith('store')) path = '/vendors';
    else if (lower.startsWith('customer') || lower.includes('@')) path = '/users';
    navigate(`${path}?search=${encodeURIComponent(q)}`);
  };

  // ⌘K / Ctrl+K focuses the search box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="h-[60px] surface-glass border-b border-slate-100 flex items-center gap-3 px-4 sm:px-6 flex-shrink-0 sticky top-0 z-30">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-1.5 -ml-1 rounded-md text-slate-600 hover:bg-slate-100"
      >
        <Menu className="h-5 w-5" />
      </button>

      <form onSubmit={runSearch} className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products, orders, vendors, customers…"
          className="pl-9 h-9 bg-slate-50 border-slate-200 focus-visible:bg-white"
        />
        <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-mono text-slate-500">
          ⌘K
        </kbd>
      </form>

      <div className="flex-1" />

      <a
        href={STOREFRONT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        title="Open the live storefront"
      >
        <ExternalLink className="h-4 w-4" /> View website
      </a>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-[18px] w-[18px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Notifications</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            You’re all caught up. New vendor applications and orders will show up here.
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-slate-100 transition-colors">
            <Avatar className="h-8 w-8">
              <AvatarFallback>{admin?.name?.[0]?.toUpperCase() || 'A'}</AvatarFallback>
            </Avatar>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-slate-900 leading-tight">{admin?.name}</p>
              <p className="text-[11px] text-slate-500 capitalize leading-tight">{admin?.role?.replace('_', ' ')}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings')}><Settings className="h-4 w-4" /> Settings</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-700 focus:bg-red-50">
            <LogOut className="h-4 w-4" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin } = useAdminStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Staff who can't see the Dashboard (no analytics.view) shouldn't land on a
  // page that 403s — send them to their first accessible nav item.
  useEffect(() => {
    if (location.pathname !== '/') return;
    const dashboard = NAV_SECTIONS[0].items[0];
    if (canSeeNav(dashboard, admin)) return;
    const firstAccessible = NAV_SECTIONS.flatMap((s) => s.items).find(
      (item) => item.to !== '/' && canSeeNav(item, admin),
    );
    if (firstAccessible) navigate(firstAccessible.to, { replace: true });
  }, [location.pathname, admin, navigate]);

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
